import type React from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  DeleteOutline,
  DescriptionOutlined,
} from "@mui/icons-material";
import { DocumentItem } from "../../../types/documents";

interface DocumentsListProps {
  documents: DocumentItem[];
  onAddDocument: () => void;
  onViewDocument: (documentField: string) => void;
  onDeleteDocument: (id: string) => void;
  hasDeletePermission: boolean;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  onAddDocument,
  onViewDocument,
  onDeleteDocument,
  hasDeletePermission,
}) => {
  const theme = useTheme();

  return (
    <Paper sx={{ p: 2, height: "100%", boxShadow: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: "bold" }}>
          Documents
        </Typography>
        <IconButton
          onClick={onAddDocument}
          sx={{
            bgcolor: theme.palette.grey[100],
            color: theme.palette.text.secondary,
            "&:hover": {
              bgcolor: theme.palette.grey[200],
            },
          }}
        >
          <AddCircleOutlineOutlined />
        </IconButton>
      </Box>

      {documents.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: "center",
            mt: 2,
          }}
        >
          No documents found
        </Typography>
      ) : (
        <List disablePadding>
          {documents.map((doc) => (
            <ListItem
              key={doc.id}
              onClick={() => onViewDocument(doc.document_field)}
              sx={{
                mb: 1,
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                  cursor: "pointer",
                },
                p: 1.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DescriptionOutlined
                  sx={{ color: theme.palette.text.secondary }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 14,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {doc.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {doc.document_field.split("/").pop()}
                    </Typography>
                  </>
                }
              />

              {hasDeletePermission && (
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteDocument(doc.id);
                  }}
                  sx={{
                    color: theme.palette.error.main,
                    ml: 1,
                  }}
                >
                  <DeleteOutline />
                </IconButton>
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default DocumentsList;
